import { message } from "antd";
import { atom } from "jotai";
import { nanoid } from "nanoid";

// state
const _bill = atom({
  billNumber: "",
  billTo: "",
  billAddress: "",
  billSubject: "",
  discountPercentage: 0,
  note: "",
  dueDate: null,
  billDate: null, // custom bill date when facility is in REACT_APP_EDITABLE_BILL_FACILITIES
  patientId: null,
  discount: 0,
  discountType: "value", // 'value' or 'percent'
  paymentReference: "",
  membershipId: null,
  redeemedBenefits: [], // Array to store redeemed benefits
  // amount_paid: 0,
});
const _items = atom([]);
const _payments = atom([]);
const _services = atom([]);
const _redeemedBenefits = atom([]); // New atom for redeemed benefits
const _charges = atom([]);
const _draftBillId = atom(null); // New atom for storing draft bill ID

// methods
// export const sumTotal = cedisLocale.format( parseFloat( ( subTotal + totalCharges ) - sale.discount ) )

function itemRate(item, services) {
  const fromItem = item.rate != null && item.rate !== "" && !Number.isNaN(parseFloat(item.rate));
  return fromItem
    ? parseFloat(item.rate)
    : (services.find((s) => s.id === item.serviceId)?.baseRate ?? 0);
}

export const getBillSubTotalAtom = atom((get) => {
  let subtotal = 0;
  const items = get(_items);
  const services = get(_services);
  items.forEach((item) => {
    const rate = itemRate(item, services);
    const quantity = parseInt(item.quantity || 0);
    subtotal += parseFloat(rate) * quantity;
  });
  return subtotal;
});

export const getTotalItemDiscountsAtom = atom((get) => {
  let totalItemDiscounts = 0;
  const items = get(_items);
  const services = get(_services);
  items.forEach((item) => {
    const rate = itemRate(item, services);
    const quantity = parseInt(item.quantity || 0);
    const itemSubtotal = parseFloat(rate) * quantity;

    const itemDiscount = parseFloat(item.discount || 0);
    const itemDiscountType = item.discountType || "value";
    const itemDiscountAmount =
      itemDiscountType === "percent"
        ? (itemSubtotal * itemDiscount) / 100
        : itemDiscount;

    totalItemDiscounts += itemDiscountAmount;
  });
  return totalItemDiscounts;
});

export const getTotalBillDiscountAtom = atom((get) => {
  const bill = get(_bill);
  const subtotal = get(getBillSubTotalAtom);
  const billDiscount = parseFloat(bill.discount || 0);
  const billDiscountType = bill.discountType || "value";

  const billDiscountAmount =
    billDiscountType === "percent"
      ? (subtotal * billDiscount) / 100
      : billDiscount;

  return billDiscountAmount;
});

export const getTotalDiscountAtom = atom((get) => {
  const itemDiscounts = get(getTotalItemDiscountsAtom);
  const billDiscount = get(getTotalBillDiscountAtom);

  return itemDiscounts + billDiscount;
});

export const getTotalPaymentsAtom = atom((get) => {
  let total = 0;
  get(_payments).map((c) => {
    total += parseFloat(c.amount || 0);
  });
  return total;
});

export const getTotalChargesAtom = atom((get) => {
  let total = 0;
  const charges = get(_charges);

  charges.forEach((charge) => {
    if (charge.chargeId && charge.value) {
      total += parseFloat(charge.value || 0);
    }
  });

  return total;
});

export const getSumAtom = atom((get) => {
  let subtotal = 0;
  const items = get(_items);
  const services = get(_services);
  items.forEach((item) => {
    const rate = itemRate(item, services);
    const quantity = parseInt(item.quantity || 0);
    const itemSubtotal = parseFloat(rate) * quantity;

    const itemDiscount = parseFloat(item.discount || 0);
    const itemDiscountType = item.discountType || "value";
    const itemDiscountAmount =
      itemDiscountType === "percent"
        ? (itemSubtotal * itemDiscount) / 100
        : itemDiscount;

    subtotal += itemSubtotal - itemDiscountAmount;
  });

  const discount = parseFloat(get(_bill)?.discount) || 0;
  const totalCharges = get(getTotalChargesAtom);
  return parseFloat(subtotal + totalCharges - discount);
});

// New comprehensive total due calculation
export const getTotalDueAtom = atom((get) => {
  const subtotal = get(getBillSubTotalAtom);
  const totalCharges = get(getTotalChargesAtom);
  const totalDiscount = get(getTotalDiscountAtom);

  // Total Due = Subtotal + Charges - Total Discounts
  return parseFloat(subtotal + totalCharges - totalDiscount);
});

// New balance calculation
export const getBalanceAtom = atom((get) => {
  const totalDue = get(getTotalDueAtom);
  const totalPayments = get(getTotalPaymentsAtom);

  return parseFloat(totalDue - totalPayments);
});

// atoms
export const billAtom = atom(
  (get) => get(_bill),
  (get, set, bill) => {
    set(_bill, { ...get(_bill), ...bill });
  }
);

export const servicesAtom = atom(
  (get) => get(_services),
  (get, set, services) => {
    set(_services, services);
  }
);

export const billItemsAtom = atom(
  (get) => get(_items)
  // ( get, set, item ) => {
  //     const items = get( _items );
  //     const curItem = items.find( it => it.recordId === item.recordId );

  //     if ( curItem )
  //         set( items, items.map( p => {
  //             if ( p.id === item.id ) {
  //                 p.quantity += 1;
  //                 return p;
  //             }
  //             else
  //                 return p;
  //         } ) );
  //     else
  //         set( _items, [ ...items, { recordId: nanoid(), serviceId: null, rate: 0, quantity: 0 } ] );
  // }
);

export const paymentsAtom = atom(
  (get) => get(_payments),
  (get, set, params) => {
    // If params is an array, it means we're setting the entire payments array
    if (Array.isArray(params)) {
      set(_payments, params);
      return;
    }

    // If params is an object with recordId, update existing payment
    if (params.recordId) {
      const currentPayments = get(_payments);
      const updatedPayments = currentPayments.map((p) =>
        p.recordId === params.recordId ? { ...p, ...params } : p
      );

      // If amount is 0 or negative, remove the payment
      if (params.amount !== undefined && params.amount <= 0) {
        set(
          _payments,
          updatedPayments.filter((p) => p.recordId !== params.recordId)
        );
      } else {
        set(_payments, updatedPayments);
      }
      return;
    }

    // Otherwise, add new payment (original behavior)
    const { paymentMethod, amount } = params;
    set(_payments, [
      ...get(_payments),
      {
        recordId: nanoid(),
        paymentMethod: paymentMethod || "",
        amount: amount || 0,
      },
    ]);
  }
);

export const redeemedBenefitsAtom = atom(
  (get) => get(_redeemedBenefits),
  (get, set, redeemedBenefits) => {
    set(_redeemedBenefits, redeemedBenefits);
  }
);

// selected products handlers
export const addItemAtom = atom(null, (get, set, param) => {
  set(_items, [
    ...get(_items),
    {
      recordId: nanoid(),
      serviceId: null,
      rate: 0,
      quantity: 1,
      discount: 0,
      discountType: "value",
    },
  ]);
});

export const deleteItemAtom = atom(null, (get, set, recordId) => {
  set(
    _items,
    get(_items).filter((itm) => itm.recordId !== recordId)
  );
});

export const updateItemAtom = atom(null, (get, set, params) => {
  let { recordId, field, value } = params;

  let item = get(_items).find((pr) => pr.recordId === recordId);

  // if ( [ 'rate' ].find( f => f === field ) )
  if (field === "quantity" && value < 0) value = 0;

  if (
    field === "serviceId" &&
    get(_items).find((itm) => itm.serviceId === value)
  ) {
    message.error("already selected!");
    return;
  }

  if (field === "serviceId")
    item.rate = get(_services).find((s) => s.id === value)?.baseRate;

  item[field] = value;
  set(
    _items,
    get(_items).map((itm) => {
      if (itm.recordId === recordId) return item;

      return itm;
    })
  );
});

// payments handler
export const deletePaymentAtom = atom(null, (get, set, recordId) => {
  set(
    _payments,
    get(_payments).filter((pay) => pay.recordId !== recordId)
  );
});

export const updatePaymentAtom = atom(null, (get, set, params) => {
  const { recordId, field, value } = params;

  // prevent duplicate methods
  if (
    field === "paymentMethod" &&
    get(_payments).find((pay) => pay.paymentMethod === value)
  ) {
    message.error("method already selected!");
    return;
  }

  const updatedPayment = get(_payments).find(
    (pay) => pay.recordId === recordId
  );
  updatedPayment[field] = value;

  // update payments
  set(
    _payments,
    get(_payments).map((pay) => {
      if (pay.recordId === recordId) return updatedPayment;
      return pay;
    })
  );
});

// get
export const getCompleteBillAtom = atom((get) => {
  const bill = get(_bill);
  const payments = get(_payments);
  const items = get(_items);
  const charges = get(_charges);
  const redeemedBenefits = get(_redeemedBenefits);
  const totalItemDiscounts = get(getTotalItemDiscountsAtom);
  const totalBillDiscount = get(getTotalBillDiscountAtom);
  const totalDiscount = get(getTotalDiscountAtom);
  const totalCharges = get(getTotalChargesAtom);
  const totalDue = get(getTotalDueAtom);
  const balance = get(getBalanceAtom);

  // Add comprehensive calculation information to the bill object
  const billWithCalculations = {
    ...bill,
    totalItemDiscounts,
    totalBillDiscount,
    totalDiscount,
    totalCharges,
    totalDue,
    balance,
  };

  return {
    bill: billWithCalculations,
    payments,
    items,
    charges,
    redeemedBenefits,
  };
});

export const resetBillAtom = atom(null, (get, set, params) => {
  set(_bill, {
    billNumber: "",
    billTo: "",
    billAddress: "",
    billSubject: "",
    discountPercentage: 0,
    note: "",
    dueDate: null,
    patientId: null,
    redeemedBenefits: [], // Reset redeemed benefits
    // amount_paid: 0,
  });
  set(_items, []);
  set(_payments, []);
  set(_charges, []); // Reset charges
  set(_redeemedBenefits, []); // Reset redeemed benefits atom
  set(_draftBillId, null); // Reset draft bill ID
});

// charges atom
export const chargesAtom = atom(
  (get) => get(_charges),
  (get, set, charges) => {
    set(_charges, charges);
  }
);

export const draftBillIdAtom = atom(
  (get) => get(_draftBillId),
  (get, set, draftBillId) => {
    set(_draftBillId, draftBillId);
  }
);

export const addChargeAtom = atom(null, (get, set, charge) => {
  set(_charges, [
    ...get(_charges),
    {
      id: nanoid(),
      chargeId: null,
      value: 0,
      ...charge,
    },
  ]);
});

export const deleteChargeAtom = atom(null, (get, set, chargeId) => {
  set(
    _charges,
    get(_charges).filter((c) => c.id !== chargeId)
  );
});

export const updateChargeAtom = atom(null, (get, set, params) => {
  const { id, ...updates } = params;
  set(
    _charges,
    get(_charges).map((c) => (c.id === id ? { ...c, ...updates } : c))
  );
});
